import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TableFooter from "@mui/material/TableFooter"
import Paper from "@mui/material/Paper"
import Pagination from "@mui/material/Pagination"
import PaginationItem from "@mui/material/PaginationItem"
import PatientDetailsTableHeadCellCard from "./AppointmentDetailsTableHeadCellCard"
import ArrowLeftIcon from "@assets/icons/arrowLeft"
import ArrowRightIcon from "@assets/icons/arrowRight"
import TableRightSideBarCell from "./TableRightSideBarCell"
import styles from "./AppointmentDetailsTable.module.css"
import useBooking from "@hooks/useBooking"
import { useEffect, useState, useCallback } from "react"
import { useSelector } from "react-redux"
import formatDateFromISOString from "src/utils/formatDate"
import { Loader } from "@components/Loader"

const AppointmentDetailsTable = () => {
  const { handleGetBooking } = useBooking()
  const { bookings: getBookingData, isGetBookingLoading } = useSelector(
    (state) => state.booking
  )
  const [paginationState, setPaginationState] = useState({
    limit: 10,
    page: 1,
  })
  // Track locally cancelled appointments
  const [locallyCancelledAppointments, setLocallyCancelledAppointments] =
    useState([])

  // Memoize the fetch function to prevent constant re-renders
  const fetchBookings = useCallback(() => {
    const requestParams = { ...paginationState }
    handleGetBooking(requestParams)
  }, [paginationState, handleGetBooking])

  useEffect(() => {
    fetchBookings()
  }, [fetchBookings])

  const getDateAndTime = (Date) => {
    const dateObj = formatDateFromISOString(Date)
    return dateObj.date + " | " + dateObj.hours + ":" + dateObj.minutes
  }

  const tableHeadCellStyles = {
    color: "var(--bg-theme-primary)",
    fontWeight: "700",
    fontSize: "12px",
    lineHeight: "16px",
    padding: "12px 24px 12px 24px",
    height: "42px",
    backgroundColor: "var(--bg-blue-light)",
  }

  const tableBodyCellStyles = {
    color: "var(--quaternary-black)",
    fontWeight: "400",
    fontSize: "12px",
    lineHeight: "16px",
    padding: "0px",
    height: "42px",
  }

  // Function to check if appointment is in the past
  const isAppointmentCompleted = (startDateTime) => {
    const appointmentDate = new Date(startDateTime)
    const currentDate = new Date()
    return appointmentDate < currentDate
  }

  // Function to handle local appointment cancellation
  const handleLocalCancellation = useCallback((appointmentId) => {
    setLocallyCancelledAppointments((prev) => [...prev, appointmentId])
  }, [])

  return (
    <div className={styles.tableAndFooterWrapper}>
      <TableContainer
        onPage
        component={Paper}
        sx={{
          border: "1px solid #EAEAEA",
          borderRadius: "0px",
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
          position: "relative",
          flexGrow: 1,
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {},
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "var(--border-theme-300)",
            borderRadius: "8px",
          },
          boxShadow: "none",
          "@media (max-width:700px)": {
            borderRadius: "16px",
            "&::-webkit-scrollbar": {
              width: "0px",
              height: "0px",
            },
          },
        }}
      >
        {isGetBookingLoading ? (
          <div className="absolute top-0 bottom-0 right-0 left-0 flex items-center justify-center z-50 bg-[--pure-white]">
            <Loader />
          </div>
        ) : null}
        <div className={styles.tableAndBarWrapper}>
          <Table stickyHeader aria-label="simple table">
            <TableHead
              sx={{
                backgroundColor: "var(--bg-blue-light)",
                "& th": {},
              }}
            >
              <TableRow>
                <TableCell sx={tableHeadCellStyles}>
                  <PatientDetailsTableHeadCellCard name="Date | Time" />
                </TableCell>
                <TableCell sx={tableHeadCellStyles} align="left">
                  <PatientDetailsTableHeadCellCard
                    name="Duration"
                    sortPath="duration"
                    setPaginationState={setPaginationState}
                  />
                </TableCell>
                <TableCell sx={tableHeadCellStyles} align="left">
                  <PatientDetailsTableHeadCellCard
                    name="First name"
                    sortPath="firstName"
                    setPaginationState={setPaginationState}
                  />
                </TableCell>
                <TableCell sx={tableHeadCellStyles} align="left">
                  <PatientDetailsTableHeadCellCard
                    name="Last name"
                    sortPath="lastName"
                    setPaginationState={setPaginationState}
                  />
                </TableCell>
                <TableCell sx={tableHeadCellStyles} align="left">
                  <PatientDetailsTableHeadCellCard
                    name="Contact"
                    sortPath="contact"
                    setPaginationState={setPaginationState}
                  />
                </TableCell>
                <TableCell sx={tableHeadCellStyles} align="left">
                  <PatientDetailsTableHeadCellCard
                    name="Location"
                    sortPath="location"
                    setPaginationState={setPaginationState}
                  />
                </TableCell>
                <TableCell sx={tableHeadCellStyles} align="left">
                  <PatientDetailsTableHeadCellCard
                    name="Type"
                    sortPath="type"
                    setPaginationState={setPaginationState}
                  />
                </TableCell>
                <TableCell sx={tableHeadCellStyles} align="left">
                  <PatientDetailsTableHeadCellCard
                    name="status"
                    sortPath="status"
                    setPaginationState={setPaginationState}
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getBookingData?.data?.map((row, index) => {
                // Check if this appointment is locally cancelled
                const isLocallyCancelled =
                  locallyCancelledAppointments.includes(row.id)
                // Use either the server status or locally cancelled status
                const effectiveStatus = isLocallyCancelled
                  ? "cancelled"
                  : row.status

                return (
                  <TableRow
                    key={row.date}
                    //   sx={{
                    //     "&:last-child td, &:last-child th": { border: 0 },
                    //   }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={tableBodyCellStyles}
                    >
                      <div className={styles.tableCellContent}>
                        {getDateAndTime(row.startDateTime)}
                      </div>
                    </TableCell>
                    <TableCell align="left" sx={tableBodyCellStyles}>
                      <div className={styles.tableCellContent}>
                        {row.duration}
                      </div>
                    </TableCell>
                    <TableCell align="left" sx={tableBodyCellStyles}>
                      <div className={styles.tableCellContent}>
                        {row.firstName}
                      </div>
                    </TableCell>
                    <TableCell align="left" sx={tableBodyCellStyles}>
                      <div className={styles.tableCellContent}>
                        {row.lastName}
                      </div>
                    </TableCell>
                    <TableCell align="left" sx={tableBodyCellStyles}>
                      <div className={styles.tableCellContent}>
                        {row.contact}
                      </div>
                    </TableCell>
                    <TableCell align="left" sx={tableBodyCellStyles}>
                      <div className={styles.tableCellContent}>
                        {row.location}
                      </div>
                    </TableCell>
                    <TableCell align="left" sx={tableBodyCellStyles}>
                      <div className={styles.tableCellContent}>{row.type}</div>
                    </TableCell>
                    <TableCell align="left" sx={tableBodyCellStyles}>
                      {effectiveStatus === "cancelled" ? (
                        <div
                          className={`${styles.planActive} ${styles.planCancelled}`}
                        >
                          <div
                            className={`${styles.planActiveDot} ${styles.planCancelledDot}`}
                          ></div>
                          Cancelled
                        </div>
                      ) : isAppointmentCompleted(row.startDateTime) ? (
                        <div
                          className={`${styles.planActive} ${styles.planCompleted}`}
                        >
                          <div
                            className={`${styles.planActiveDot} ${styles.planCompletedDot}`}
                          ></div>
                          Completed
                        </div>
                      ) : (
                        <div className={styles.planActive}>
                          <div className={styles.planActiveDot}></div>
                          Upcoming
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          <div className={styles.tableRightSideBar}>
            <div className={styles.rightSideBarHeadCell}></div>

            {getBookingData?.data?.map((booking) => {
              return (
                <TableRightSideBarCell
                  key={booking.id}
                  appointment={booking}
                  onLocalCancel={handleLocalCancellation}
                />
              )
            })}
          </div>
        </div>
      </TableContainer>
      <TableFooter
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          border: "1px solid #EAEAEA",
          backgroundColor: "white",
          borderBottomLeftRadius: "16px",
          borderBottomRightRadius: "16px",
          padding: "5px",

          "@media (max-width:1215px)": {
            flexDirection: "column",
            gap: "10px",
            width: "100%",
          },
          "@media (max-width:700px)": {
            backgroundColor: "transparent",
            border: "none",
          },
        }}
      >
        <div className={styles.rowsPerPageSection}>
          <h3>Rows per page</h3>
          <select
            className={styles.rowsPerPageOptionsWrapper}
            onChange={(event) => {
              const value = event.currentTarget.value
              setPaginationState({ page: 1, limit: value })
            }}
            value={paginationState.limit}
            disabled={isGetBookingLoading ? true : false}
          >
            <option value="10">1-10</option>
            <option value="20">1-20</option>
            <option value="30">1-30</option>
          </select>
          <h3>of patient details</h3>
        </div>

        <div className={styles.mobilePaginationSection}>
          <button
            disabled={isGetBookingLoading ? true : false}
            className={styles.mobilePaginationPreviousButton}
            onClick={() => {
              if (parseInt(paginationState.page) - 1 >= 1) {
                setPaginationState((previous) => {
                  return {
                    ...previous,
                    page: parseInt(previous.page) - 1,
                  }
                })
              }
            }}
          >
            <ArrowLeftIcon stroke="var(--pure-white)" />
            Previous
          </button>
          <select
            className={styles.rowsPerPageOptionsWrapper}
            onChange={(event) => {
              const value = event.currentTarget.value
              setPaginationState({ page: 1, limit: value })
            }}
            value={paginationState.limit}
            disabled={isGetBookingLoading ? true : false}
          >
            <option value="10">1-10</option>
            <option value="20">1-20</option>
            <option value="30">1-30</option>
          </select>
          <button
            disabled={isGetBookingLoading ? true : false}
            className={styles.mobilePaginationNextButton}
            onClick={() => {
              if (
                parseInt(paginationState.page) + 1 <=
                Math.ceil(getBookingData?.total_record / paginationState.limit)
              ) {
                setPaginationState((previous) => {
                  return {
                    ...previous,
                    page: parseInt(previous.page) + 1,
                  }
                })
              }
            }}
          >
            Next
            <ArrowRightIcon stroke="var(--pure-white)" />
          </button>
        </div>
        <Pagination
          disabled={isGetBookingLoading ? true : false}
          count={Math.ceil(
            getBookingData?.total_record / paginationState.limit
          )}
          defaultPage={1}
          siblingCount={1}
          boundaryCount={1}
          onChange={(event, page) => {
            setPaginationState((previous) => {
              return { ...previous, page }
            })
          }}
          sx={{
            "@media (max-width:700px)": {
              display: "none",
            },
          }}
          renderItem={(item) => (
            <PaginationItem
              components={{
                previous: () => (
                  <div className={styles.paginationPreviousButton}>
                    <ArrowLeftIcon />
                    Previous
                  </div>
                ),
                next: () => (
                  <div className={styles.paginationNextButton}>
                    Next
                    <ArrowRightIcon />
                  </div>
                ),
              }}
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "var(--primary-blue-light)",
                },
              }}
              {...item}
            />
          )}
          shape="rounded"
        />
      </TableFooter>
    </div>
  )
}

export default AppointmentDetailsTable
